import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Target, 
  TrendingUp, 
  TrendingDown,
  Phone,
  Users,
  CheckCircle,
  Clock,
  AlertTriangle,
  Star,
  ThumbsUp,
  Calendar,
  Mail,
  MessageSquare,
  Activity
} from 'lucide-react';

interface UserPerformanceCardsProps {
  // Activity-based metrics
  totalActivities: number;
  todaysActivities: number;
  callsAttempted: number;
  callsReached: number;
  emailsSent: number;
  smsSent: number;
  leadsContacted: number;
  leadsCreated: number;
  leadsAssigned: number;
  callSuccessRate: number;
  conversionRate: number;
  // Task tracking
  tasksCompletedCount: number;
  tasksRemainingCount: number;
  totalTasksCount: number;
  todaysTasksCount: number;
  todaysCompletedTasksCount: number;
  todaysRemainingTasksCount: number;
  // Event tracking
  totalEventsCount: number;
  todaysEventsCount: number;
  todaysCompletedEventsCount: number;
  todaysRemainingEventsCount: number;
}

const UserPerformanceCards: React.FC<UserPerformanceCardsProps> = ({
  totalActivities,
  todaysActivities,
  callsAttempted,
  callsReached,
  emailsSent,
  smsSent,
  leadsContacted,
  leadsCreated,
  leadsAssigned,
  callSuccessRate,
  conversionRate,
  tasksCompletedCount,
  tasksRemainingCount,
  totalTasksCount,
  todaysTasksCount,
  todaysCompletedTasksCount,
  todaysRemainingTasksCount,
  totalEventsCount,
  todaysEventsCount,
  todaysCompletedEventsCount,
  todaysRemainingEventsCount
}) => {
  const getTrendIcon = (value: number, threshold: number = 0) => {
    if (value > threshold) {
      return <TrendingUp className="h-3 w-3 text-green-600" />;
    }
    return <TrendingDown className="h-3 w-3 text-red-600" />;
  };

  const getConversionBadge = (rate: number) => {
    if (rate >= 30) return { variant: 'default' as const, label: 'Excellent', icon: Star };
    if (rate >= 20) return { variant: 'secondary' as const, label: 'Good', icon: ThumbsUp };
    if (rate >= 10) return { variant: 'outline' as const, label: 'Average', icon: Target };
    return { variant: 'destructive' as const, label: 'Needs Improvement', icon: AlertTriangle };
  };

  const conversionBadge = getConversionBadge(conversionRate);

  // Helper function to get appropriate task message
  const getTaskMessage = () => {
    if (totalTasksCount === 0) {
      return "No tasks created yet";
    }
    if (todaysTasksCount === 0) {
      return `${totalTasksCount} total tasks, none due today`;
    }
    if (todaysRemainingTasksCount === 0 && todaysTasksCount > 0) {
      return "All tasks done! 🎉";
    }
    const completionRate = todaysTasksCount > 0 
      ? Math.round((todaysCompletedTasksCount / todaysTasksCount) * 100) 
      : 0;
    return `${completionRate}% complete for today`;
  };

  // Helper function to get appropriate event message
  const getEventMessage = () => {
    if (totalEventsCount === 0) {
      return "No events created yet";
    }
    if (todaysEventsCount === 0) {
      return `${totalEventsCount} total events, none today`;
    }
    if (todaysRemainingEventsCount === 0 && todaysEventsCount > 0) {
      return "All events done! 🎉";
    }
    const completionRate = todaysEventsCount > 0 
      ? Math.round((todaysCompletedEventsCount / todaysEventsCount) * 100) 
      : 0;
    return `${completionRate}% complete for today`;
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
      {/* Activities Today */}
      <Card className="h-full">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Activities Today</CardTitle>
          <Activity className="h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground" />
        </CardHeader>
        <CardContent className="pb-4">
          <div className="flex items-center justify-between">
            <div className="text-2xl sm:text-3xl font-bold">{todaysActivities}</div>
            <div className="flex items-center gap-1">
              {getTrendIcon(todaysActivities, 5)}
              <span className="text-xs sm:text-sm text-muted-foreground">
                {todaysActivities > 5 ? '+' : ''}{Math.max(0, todaysActivities - 5)}
              </span>
            </div>
          </div>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1">
            {todaysActivities >= 5 ? 'Great progress!' : `${5 - todaysActivities} more to target`}
          </p>
        </CardContent>
      </Card>

      {/* Calls Performance */}
      <Card className="h-full">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Calls</CardTitle>
          <Phone className="h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground" />
        </CardHeader>
        <CardContent className="pb-4">
          <div className="flex items-center justify-between">
            <div className="text-2xl sm:text-3xl font-bold">{callsAttempted}</div>
            <Badge variant={callSuccessRate > 0 ? "default" : "outline"} className="text-xs sm:text-sm px-2 py-1">
              {callSuccessRate}% success
            </Badge>
          </div>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1">
            {callsReached} successful calls
          </p>
        </CardContent>
      </Card>

      {/* Communications */}
      <Card className="h-full">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Communications</CardTitle>
          <Mail className="h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground" />
        </CardHeader>
        <CardContent className="pb-4">
          <div className="flex items-center justify-between">
            <div className="text-2xl sm:text-3xl font-bold">{emailsSent + smsSent}</div>
            <div className="flex items-center gap-1 text-xs sm:text-sm text-muted-foreground">
              <Mail className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">{emailsSent}</span>
              <span className="sm:hidden">{emailsSent}</span>
              <MessageSquare className="h-3 w-3 sm:h-4 sm:w-4 ml-1" />
              <span className="hidden sm:inline">{smsSent}</span>
              <span className="sm:hidden">{smsSent}</span>
            </div>
          </div>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1">
            {emailsSent} emails, {smsSent} SMS
          </p>
        </CardContent>
      </Card>

      {/* Leads Contacted */}
      <Card className="h-full">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Leads Contacted</CardTitle>
          <Users className="h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground" />
        </CardHeader>
        <CardContent className="pb-4">
          <div className="flex items-center justify-between">
            <div className="text-2xl sm:text-3xl font-bold">{leadsContacted}</div>
            <Badge variant={conversionRate > 0 ? "default" : "outline"} className="text-xs sm:text-sm px-2 py-1">
              {conversionRate}% conversion
            </Badge>
          </div>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1">
            {leadsCreated} leads created
          </p>
        </CardContent>
      </Card>

      {/* Tasks Status */}
      <Card className="h-full">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Tasks Today</CardTitle>
          <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground" />
        </CardHeader>
        <CardContent className="pb-4">
          <div className="flex items-center justify-between">
            <div className="text-2xl sm:text-3xl font-bold">{todaysTasksCount}</div>
            <div className="flex items-center gap-1 text-xs sm:text-sm text-muted-foreground">
              <Clock className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">{todaysRemainingTasksCount} remaining</span>
              <span className="sm:hidden">{todaysRemainingTasksCount}</span>
            </div>
          </div>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1 truncate">
            {getTaskMessage()}
          </p>
        </CardContent>
      </Card>

      {/* Events Today */}
      <Card className="h-full">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Events Today</CardTitle>
          <Calendar className="h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground" />
        </CardHeader>
        <CardContent className="pb-4">
          <div className="flex items-center justify-between">
            <div className="text-2xl sm:text-3xl font-bold">{todaysEventsCount}</div>
            <div className="flex items-center gap-1 text-xs sm:text-sm text-muted-foreground">
              <Clock className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">{todaysRemainingEventsCount} remaining</span>
              <span className="sm:hidden">{todaysRemainingEventsCount}</span>
            </div>
          </div>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1 truncate">
            {getEventMessage()}
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default UserPerformanceCards;